import React, { useState, useEffect, useCallback } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate } from 'react-router-dom';
import { complianceService, arrearsService, tenantService, awaabService } from '../../services/api';
import LegalUpdates from '../Dashboard/components/LegalUpdates';
import ExceptionFeed from './ExceptionFeed';
import { DepositTrackerWidget, RekeyStatusWidget, RentalRegistrationWidget } from '../TexasCompliance';
import { featureFlags } from '../../utils/featureFlags';
import { getAppMarket } from '../../services/MarketService';
import { addressToText } from '../../utils/formatAddress';

const WIDGET_CONFIG_KEY = 'hp_widget_order';

interface WidgetDefinition {
    id: string;
    title: string;
    icon: string;
    defaultVisible: boolean;
}

const AVAILABLE_WIDGETS: WidgetDefinition[] = [
    { id: 'exposure-score', title: 'Portfolio Legal Exposure Score', icon: 'fa-balance-scale', defaultVisible: true },
    { id: 'predictive-alerts', title: 'Urgent Action Cards', icon: 'fa-crystal-ball', defaultVisible: true },
    { id: 'portfolio-health', title: 'Total Portfolio Health', icon: 'fa-shield-check', defaultVisible: true },
    { id: 'live-exceptions', title: 'Live Exceptions', icon: 'fa-triangle-exclamation', defaultVisible: true },
    { id: 'legal-updates', title: 'Legal Updates', icon: 'fa-gavel', defaultVisible: true },
    { id: 'rent-arrears', title: 'Rent Arrears & Vacant Properties', icon: 'fa-money-bill-trend-up', defaultVisible: true },
    { id: 'recent-activity', title: 'Recent Activity', icon: 'fa-clock-rotate', defaultVisible: true },
    // Super Admin per-account toggle (Feature Toggles > Compliance Reporting
    // Widget) — gated separately below, not just personal show/hide.
    { id: 'repairs-widget', title: 'Repairs', icon: 'fa-toolbox', defaultVisible: true },
    { id: 'awaab-law-widget', title: "Awaab's Law", icon: 'fa-house-chimney-crack', defaultVisible: true },
    // Texas Compliance Widgets (F-268, Epic E-049)
    ...(getAppMarket() === 'texas'
        ? [
            { id: 'texas-deposit-tracker', title: 'Texas Deposit Return Tracker', icon: 'fa-hand-holding-dollar', defaultVisible: true },
            { id: 'texas-rekey-status', title: 'Texas 7-Day Rekey Status', icon: 'fa-key', defaultVisible: true },
            { id: 'texas-rental-registration', title: 'Texas Rental Registrations', icon: 'fa-city', defaultVisible: true },
        ]
        : []),
];

function loadWidgetOrder(): string[] {
    try {
        const saved = localStorage.getItem(WIDGET_CONFIG_KEY);
        if (saved) {
            const parsed: string[] = JSON.parse(saved);
            // Migrate: append any AVAILABLE_WIDGETS entry the user doesn't
            // have saved yet (e.g. a widget shipped after they'd already
            // customised their order) so it doesn't stay invisible forever.
            const existing = new Set(parsed);
            const missing = AVAILABLE_WIDGETS.map(w => w.id).filter(id => !existing.has(id));
            return missing.length ? [...parsed, ...missing] : parsed;
        }
    } catch { }
    return AVAILABLE_WIDGETS.map(w => w.id);
}

function loadWidgetVisibility(): Record<string, boolean> {
    try {
        const saved = localStorage.getItem('hp_widget_visibility');
        if (saved) return JSON.parse(saved);
    } catch { }
    return {};
}

function saveWidgetOrder(order: string[]) {
    localStorage.setItem(WIDGET_CONFIG_KEY, JSON.stringify(order));
}

function saveWidgetVisibility(visibility: Record<string, boolean>) {
    localStorage.setItem('hp_widget_visibility', JSON.stringify(visibility));
}

function SortableWidget({ id, children }: { id: string; children: React.ReactNode }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div ref={setNodeRef} style={style} className="flex gap-2 group">
            <div
                {...attributes}
                {...listeners}
                className="flex items-center cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-600 transition-colors pt-4 px-1 opacity-50 group-hover:opacity-100"
            >
                <i className="fas fa-grip-vertical"></i>
            </div>
            <div className="flex-1 min-w-0">
                {children}
            </div>
        </div>
    );
}

function SortableConfigItem({ id, idx, widget, isVisible, onToggle }: { id: string, idx: number, widget: any, isVisible: boolean, onToggle: (id: string) => void }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    
    return (
        <div ref={setNodeRef} style={style} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200">
            <div className="flex items-center gap-3">
                <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-700 px-1">
                    <i className="fas fa-bars"></i>
                </div>
                <span className="text-gray-400 text-xs font-mono w-4 text-right">{idx + 1}</span>
                <i className={`fas ${widget.icon} text-emerald-500`}></i>
                <span className="font-medium text-gray-900 text-sm">{widget.title}</span>
            </div>
            <button
                onClick={() => onToggle(id)}
                className={`relative w-10 h-5 rounded-full transition-colors ${isVisible !== false ? 'bg-emerald-500' : 'bg-gray-300'}`}
            >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isVisible !== false ? 'translate-x-5' : ''}`}></span>
            </button>
        </div>
    );
}

export function CompactWidget({ title, icon, iconColor, badge, children, defaultExpanded = false }: {
    title: string;
    icon: string;
    iconColor: string;
    badge?: React.ReactNode;
    children: React.ReactNode;
    defaultExpanded?: boolean;
}) {
    const [expanded, setExpanded] = useState(defaultExpanded);

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div
                className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center gap-2">
                    <i className={`fas ${icon} text-${iconColor}-500 text-sm`}></i>
                    <h3 className="text-sm font-bold text-gray-900">{title}</h3>
                </div>
                <div className="flex items-center gap-2">
                    {badge}
                    <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} text-gray-400 text-xs transition-transform`}></i>
                </div>
            </div>
            {expanded && (
                <div className="border-t border-gray-100 px-4 py-3">
                    {children}
                </div>
            )}
        </div>
    );
}

function ExposureScoreWidget({ exposureScore }: { exposureScore: any }) {
    if (!exposureScore) return (
        <CompactWidget title="Portfolio Legal Exposure Score" icon="fa-balance-scale" iconColor="indigo" badge={<span className="text-xs text-gray-400">No data</span>}>
            <p className="text-xs text-gray-500">No exposure data available</p>
        </CompactWidget>
    );

    const score = exposureScore.portfolioScore || 0;
    const status = exposureScore.status || 'Unknown';
    const statusColor = score >= 90 ? 'green' : score >= 70 ? 'blue' : score >= 50 ? 'yellow' : 'red';

    return (
        <CompactWidget
            title="Portfolio Legal Exposure Score"
            icon="fa-balance-scale"
            iconColor="indigo"
            badge={<span className={`px-2 py-0.5 rounded-full text-[10px] font-bold bg-${statusColor}-100 text-${statusColor}-700`}>{status}</span>}
        >
            <div className="flex items-center gap-3 mb-3">
                <div className="text-3xl font-black text-gray-900">{score}%</div>
                <div className="flex-1">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className={`h-2 rounded-full transition-all duration-500 bg-${statusColor}-500`} style={{ width: `${score}%` }}></div>
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-4 gap-2">
                {[
                    { label: 'Critical Docs', value: exposureScore.breakdown?.criticalDocs || 0, max: 50, color: 'indigo' },
                    { label: 'Syntax', value: exposureScore.breakdown?.syntaxWording || 0, max: 20, color: 'purple' },
                    { label: 'Investigatory', value: exposureScore.breakdown?.investigatory || 0, max: 20, color: 'blue' },
                    { label: 'Admin', value: exposureScore.breakdown?.adminHygiene || 0, max: 10, color: 'teal' }
                ].map((p, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-2 text-center">
                        <div className="text-[10px] font-semibold text-gray-600">{p.label}</div>
                        <div className="text-sm font-bold text-gray-900">{p.value}<span className="text-[9px] text-gray-400">/{p.max}</span></div>
                    </div>
                ))}
            </div>
        </CompactWidget>
    );
}

function PredictiveAlertsWidget({ predictiveAlerts }: { predictiveAlerts: any }) {
    const alerts = predictiveAlerts?.alerts || [];
    const summary = predictiveAlerts?.summary || {};

    if (!alerts.length) return (
        <CompactWidget title="Urgent Action Cards" icon="fa-crystal-ball" iconColor="amber" badge={<span className="text-xs text-gray-400">All clear</span>}>
            <p className="text-xs text-gray-500">No urgent actions required</p>
        </CompactWidget>
    );

    return (
        <CompactWidget
            title="Urgent Action Cards"
            icon="fa-crystal-ball"
            iconColor="amber"
            badge={
                <div className="flex gap-1">
                    {summary.red > 0 && <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full text-[10px] font-bold">{summary.red}</span>}
                    {summary.amber > 0 && <span className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full text-[10px] font-bold">{summary.amber}</span>}
                    {summary.yellow > 0 && <span className="bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full text-[10px] font-bold">{summary.yellow}</span>}
                </div>
            }
        >
            <div className="space-y-2 max-h-60 overflow-y-auto">
                {alerts.slice(0, 8).map((alert: any) => (
                    <div key={alert.id} className={`flex items-start gap-2 p-2 rounded-lg border ${alert.severity === 'RED' ? 'bg-red-50 border-red-200' : alert.severity === 'AMBER' ? 'bg-orange-50 border-orange-200' : 'bg-yellow-50 border-yellow-200'}`}>
                        <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${alert.severity === 'RED' ? 'bg-red-200 text-red-700' : alert.severity === 'AMBER' ? 'bg-orange-200 text-orange-700' : 'bg-yellow-200 text-yellow-700'}`}>
                            <i className={`fas ${alert.daysUntilExpiry === null ? 'fa-exclamation-triangle' : alert.daysUntilExpiry < 0 ? 'fa-times-circle' : 'fa-clock'} text-[10px]`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-gray-900 truncate">{alert.message}</div>
                            <div className="text-[10px] text-gray-500 truncate">{addressToText(alert.address)}</div>
                        </div>
                    </div>
                ))}
            </div>
        </CompactWidget>
    );
}

function PortfolioHealthWidget({ stats, properties, certificates }: { stats: any; properties: any[]; certificates: any[] }) {
    const navigate = useNavigate();
    // Read the backend's authoritative counts. The dashboard endpoint computes
    // these from the full DynamoDB scan; the in-memory `properties` and
    // `certificates` arrays passed as props are empty placeholders (the parent
    // doesn't ship them because of the response-size cap).
    const total       = stats?.totalProperties      ?? 0;
    const compliant   = stats?.compliantProperties  ?? 0;
    const atRisk      = stats?.atRiskProperties     ?? Math.max(0, total - compliant);
    const vacant      = stats?.vacantCount          ?? 0;
    const occupied    = stats?.occupiedCount        ?? Math.max(0, total - vacant);
    // Occupancy breakdown — partial HMOs (X of Y rooms filled) are counted
    // as vacant because they have available rooms. Tooltip shows the split.
    const vacantRes   = stats?.vacantResidential    ?? 0;
    const vacantHmo   = stats?.vacantHMO            ?? 0;
    const partialHmo  = stats?.partialHMO           ?? 0;
    // Compliance % from backend score field (already correctly calculated)
    const pct = stats?.complianceScore != null
        ? Math.round(stats.complianceScore)
        : (total > 0 ? Math.round((compliant / total) * 100) : 0);

    return (
        <CompactWidget
            title="Total Portfolio Health"
            icon="fa-shield-check"
            iconColor="emerald"
            badge={<span className="text-xs font-bold text-gray-700">{compliant}/{total}</span>}
        >
            <div className="flex items-center gap-3 mb-3">
                <div className="text-3xl font-black text-gray-900">{pct}%</div>
                <div className="flex-1">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="h-2 rounded-full bg-gradient-to-r from-red-500 via-yellow-500 to-green-500" style={{ width: `${pct}%` }}></div>
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
                <button
                    type="button"
                    onClick={() => navigate('/properties?filter=compliant')}
                    className="bg-emerald-50 rounded-lg p-2 text-center border border-emerald-100 hover:bg-emerald-100 hover:border-emerald-300 transition cursor-pointer"
                    title="View compliant properties"
                >
                    <div className="text-lg font-black text-emerald-700">{compliant}</div>
                    <div className="text-[10px] font-semibold text-emerald-600">Compliant</div>
                </button>
                <button
                    type="button"
                    onClick={() => navigate('/properties?filter=at_risk')}
                    className="bg-amber-50 rounded-lg p-2 text-center border border-amber-100 hover:bg-amber-100 hover:border-amber-300 transition cursor-pointer"
                    title="View properties needing action"
                >
                    <div className="text-lg font-black text-amber-700">{atRisk}</div>
                    <div className="text-[10px] font-semibold text-amber-600">At Risk</div>
                </button>
                <button
                    type="button"
                    onClick={() => navigate('/properties?filter=vacant')}
                    className="bg-slate-50 rounded-lg p-2 text-center border border-slate-200 hover:bg-slate-100 hover:border-slate-300 transition cursor-pointer"
                    title={`Vacant breakdown: ${vacantRes} residential · ${vacantHmo} HMO · ${partialHmo} HMO partial — click to view`}
                >
                    <div className="text-lg font-black text-slate-700">{vacant}</div>
                    <div className="text-[10px] font-semibold text-slate-600">Vacant / Partial</div>
                </button>
            </div>

            {/* Vacancy breakdown — only shown when there's something to split out */}
            {(vacantRes > 0 || vacantHmo > 0 || partialHmo > 0) && (
                <div className="mt-2 grid grid-cols-3 gap-1 text-[10px]">
                    <button
                        type="button"
                        onClick={() => navigate('/properties?filter=vacant_residential')}
                        className="bg-white rounded px-1.5 py-1 border border-slate-100 hover:border-slate-300 text-left cursor-pointer"
                        title="Single-let with no tenant"
                    >
                        <div className="font-bold text-slate-700">{vacantRes}</div>
                        <div className="text-slate-500">Resi vacant</div>
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/properties?filter=vacant_hmo')}
                        className="bg-white rounded px-1.5 py-1 border border-slate-100 hover:border-slate-300 text-left cursor-pointer"
                        title="HMO with zero tenants"
                    >
                        <div className="font-bold text-slate-700">{vacantHmo}</div>
                        <div className="text-slate-500">HMO empty</div>
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/properties?filter=hmo_partial')}
                        className="bg-white rounded px-1.5 py-1 border border-slate-100 hover:border-slate-300 text-left cursor-pointer"
                        title="HMO with spare rooms"
                    >
                        <div className="font-bold text-slate-700">{partialHmo}</div>
                        <div className="text-slate-500">HMO partial</div>
                    </button>
                </div>
            )}
        </CompactWidget>
    );
}

function RentArrearsWidget({ dashboardData, onTenantClick }: { dashboardData: any; onTenantClick?: (tenantId: string) => void }) {
    const navigate = useNavigate();
    const arrearsCount = dashboardData.arrearsCount || 0;
    const arrearsAlerts = dashboardData.arrearsAlerts || [];
    const totalArrears = dashboardData.totalArrears || 0;
    const collectionRate = dashboardData.collectionRate ?? 100;
    const criticalCount = dashboardData.criticalCount || 0;
    const overdueCount = dashboardData.overdueCount || 0;
    const upcomingCount = dashboardData.upcomingCount || 0;

    // Single source of truth — backend dashboard stats. The properties array
    // is loaded in the background (no _vacant flag set during first paint)
    // so deriving from it gave a stale 0 while Portfolio Health showed 5.
    // Falls back to local computation if the backend hasn't returned the
    // field yet.
    const vacantCount = dashboardData.stats?.vacantCount
        ?? (dashboardData.properties || []).filter((p: any) => p._vacant === true).length
        ?? 0;
    const vacantProperties = (dashboardData.properties || []).filter((p: any) => p._vacant === true);

    const statusStyle = (s: string) => {
        if (s === 'CRITICAL') return { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', icon: 'fa-gavel', label: 'CRITICAL' };
        if (s === 'OVERDUE')  return { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', icon: 'fa-exclamation-circle', label: 'OVERDUE' };
        if (s === 'LATE')     return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: 'fa-clock', label: 'LATE' };
        if (s === 'UPCOMING') return { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: 'fa-bell', label: 'UPCOMING' };
        return { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: 'fa-check-circle', label: 'OK' };
    };

    return (
        <CompactWidget
            title="Rent Arrears & Vacant Properties"
            icon="fa-money-bill-trend-up"
            iconColor="red"
            badge={
                <div className="flex gap-1">
                    {criticalCount > 0 && <span className="bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded-full text-[10px] font-bold">{criticalCount} critical</span>}
                    {overdueCount > 0 && <span className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full text-[10px] font-bold">{overdueCount} overdue</span>}
                    {upcomingCount > 0 && <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full text-[10px] font-bold">{upcomingCount} soon</span>}
                </div>
            }
        >
            <div className="grid grid-cols-3 gap-2 mb-3">
                <div className={`rounded-lg p-2 text-center border ${
                    totalArrears > 0 ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'
                }`}>
                    <i className={`fas fa-pound-sign ${totalArrears > 0 ? 'text-rose-500' : 'text-emerald-500'} text-sm mb-1`}></i>
                    <div className={`text-base font-black ${totalArrears > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                        £{totalArrears.toFixed(0)}
                    </div>
                    <div className="text-[9px] font-semibold text-gray-500">Arrears</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 text-center border border-gray-200">
                    <i className="fas fa-percentage text-gray-500 text-sm mb-1"></i>
                    <div className={`text-base font-black ${
                        collectionRate >= 95 ? 'text-emerald-700' :
                        collectionRate >= 80 ? 'text-amber-700' : 'text-rose-700'
                    }`}>
                        {collectionRate}%
                    </div>
                    <div className="text-[9px] font-semibold text-gray-500">Collected</div>
                </div>
                <button
                    type="button"
                    onClick={() => navigate('/properties?filter=vacant')}
                    className="bg-slate-50 rounded-lg p-2 text-center border border-slate-200 hover:bg-slate-100 hover:border-slate-300 transition cursor-pointer"
                    title="View vacant properties"
                >
                    <i className="fas fa-house-chimney-crack text-slate-500 text-sm mb-1"></i>
                    <div className="text-base font-black text-slate-700">{vacantCount}</div>
                    <div className="text-[9px] font-semibold text-gray-500">Vacant</div>
                </button>
            </div>

            {arrearsAlerts.length > 0 && (
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {arrearsAlerts.slice(0, 5).map((a: any, i: number) => {
                        const s = statusStyle(a.status);
                        return (
                            <button
                                key={i}
                                onClick={() => a.tenantId && onTenantClick?.(a.tenantId)}
                                className={`w-full text-left flex items-center gap-2 p-2 ${s.bg} rounded-lg border ${s.border} hover:shadow-sm transition-all`}
                            >
                                <i className={`fas ${s.icon} ${s.text} text-xs`}></i>
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs font-bold text-gray-900 truncate">{a.tenantName || 'Unknown tenant'}</div>
                                    <div className="text-[10px] text-gray-500 truncate">
                                        {a.recommendationTitle || s.label}
                                        {a.worstPeriodYM && ` · ${a.worstPeriodYM}`}
                                    </div>
                                </div>
                                <div className={`text-xs font-black ${s.text}`}>
                                    {a.amount > 0 ? `£${Number(a.amount).toFixed(0)}` : ''}
                                </div>
                                {a.tenantId && (
                                    <i className="fas fa-chevron-right text-[8px] text-gray-400"></i>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {arrearsCount === 0 && vacantCount === 0 && arrearsAlerts.length === 0 && (
                <div className="text-center py-3">
                    <i className="fas fa-check-circle text-emerald-500 text-lg mb-1"></i>
                    <p className="text-xs text-gray-500">No arrears or vacant properties</p>
                </div>
            )}

            {(arrearsCount > 0 || vacantCount > 0) && (
                <div className="mt-2 pt-2 border-t border-gray-100 text-center">
                    <a
                        href="/arrears"
                        className="text-[10px] font-black uppercase text-rose-600 hover:text-rose-800 tracking-tighter"
                    >
                        Open Arrears Manager →
                    </a>
                </div>
            )}
        </CompactWidget>
    );
}

function RepairsWidgetCompact() {
    const [dashboard, setDashboard] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        awaabService.getPortfolioDashboard()
            .then((res: any) => {
                if (cancelled) return;
                const data = res.data?.data;
                setDashboard({
                    open: data?.portfolio?.totalOpenCases,
                    onTrack: data?.slaHealth?.onTrack,
                    atRisk: data?.slaHealth?.atRisk,
                    breached: data?.slaHealth?.breached,
                });
            })
            .catch(() => { if (!cancelled) setDashboard(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    if (loading) return (
        <CompactWidget title="Repairs" icon="fa-toolbox" iconColor="blue" badge={<span className="text-xs text-gray-400">Loading…</span>}>
            <p className="text-xs text-gray-500">Loading repairs data…</p>
        </CompactWidget>
    );

    if (!dashboard) return (
        <CompactWidget title="Repairs" icon="fa-toolbox" iconColor="blue" badge={<span className="text-xs text-gray-400">No data</span>}>
            <p className="text-xs text-gray-500">No repairs data yet.</p>
        </CompactWidget>
    );

    return (
        <CompactWidget
            title="Repairs"
            icon="fa-toolbox"
            iconColor="blue"
            badge={<span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${dashboard.breached > 0 ? 'bg-red-100 text-red-700' : dashboard.atRisk > 0 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{dashboard.breached > 0 ? `${dashboard.breached} breached` : dashboard.atRisk > 0 ? `${dashboard.atRisk} at risk` : 'On track'}</span>}
        >
            <div className="grid grid-cols-4 gap-2">
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                    <div className="text-sm font-bold text-gray-900">{dashboard.open ?? '—'}</div>
                    <div className="text-[9px] font-semibold text-gray-500">In progress</div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-2 text-center">
                    <div className="text-sm font-bold text-emerald-700">{dashboard.onTrack ?? '—'}</div>
                    <div className="text-[9px] font-semibold text-emerald-600">On track</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-2 text-center">
                    <div className="text-sm font-bold text-amber-700">{dashboard.atRisk ?? '—'}</div>
                    <div className="text-[9px] font-semibold text-amber-600">At risk</div>
                </div>
                <div className="bg-red-50 rounded-lg p-2 text-center">
                    <div className="text-sm font-bold text-red-700">{dashboard.breached ?? '—'}</div>
                    <div className="text-[9px] font-semibold text-red-600">Breached</div>
                </div>
            </div>
        </CompactWidget>
    );
}

function AwaabLawWidgetCompact() {
    const [dashboard, setDashboard] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        awaabService.getPortfolioDashboard()
            .then((res: any) => {
                if (cancelled) return;
                const data = res.data?.data;
                setDashboard({
                    open: data?.portfolio?.totalOpenCases,
                    closed: data?.portfolio?.totalClosedCases,
                    hhsrsCat1: data?.hhsrsCat1Open,
                    highRisk: data?.highRiskOpen,
                });
            })
            .catch(() => { if (!cancelled) setDashboard(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    if (loading) return (
        <CompactWidget title="Awaab's Law" icon="fa-house-chimney-crack" iconColor="orange" badge={<span className="text-xs text-gray-400">Loading…</span>}>
            <p className="text-xs text-gray-500">Loading Awaab's Law data…</p>
        </CompactWidget>
    );

    if (!dashboard) return (
        <CompactWidget title="Awaab's Law" icon="fa-house-chimney-crack" iconColor="orange" badge={<span className="text-xs text-gray-400">No data</span>}>
            <p className="text-xs text-gray-500">No hazard cases yet.</p>
        </CompactWidget>
    );

    return (
        <CompactWidget
            title="Awaab's Law"
            icon="fa-house-chimney-crack"
            iconColor="orange"
            badge={<span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${dashboard.hhsrsCat1 > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{dashboard.hhsrsCat1 > 0 ? `${dashboard.hhsrsCat1} Cat 1` : 'No Cat 1'}</span>}
        >
            <div className="grid grid-cols-4 gap-2">
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                    <div className="text-sm font-bold text-gray-900">{dashboard.open ?? '—'}</div>
                    <div className="text-[9px] font-semibold text-gray-500">Open</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                    <div className="text-sm font-bold text-gray-900">{dashboard.closed ?? '—'}</div>
                    <div className="text-[9px] font-semibold text-gray-500">Closed</div>
                </div>
                <div className="bg-red-50 rounded-lg p-2 text-center">
                    <div className="text-sm font-bold text-red-700">{dashboard.hhsrsCat1 ?? '—'}</div>
                    <div className="text-[9px] font-semibold text-red-600">HHSRS Cat 1</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-2 text-center">
                    <div className="text-sm font-bold text-amber-700">{dashboard.highRisk ?? '—'}</div>
                    <div className="text-[9px] font-semibold text-amber-600">High risk</div>
                </div>
            </div>
        </CompactWidget>
    );
}

function WidgetConfigModal({ isOpen, onClose, widgetOrder, widgetVisibility, onToggle, onReset, onDragEnd }: {
    isOpen: boolean;
    onClose: () => void;
    widgetOrder: string[];
    widgetVisibility: Record<string, boolean>;
    onToggle: (id: string) => void;
    onReset: () => void;
    onDragEnd: (event: DragEndEvent) => void;
}) {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900">
                        <i className="fas fa-sliders text-emerald-500 mr-2"></i>
                        Configure Dashboard
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">&times;</button>
                </div>
                <p className="text-sm text-gray-500 mb-4">Toggle to show/hide widgets. Drag to reorder.</p>
                
                <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <SortableContext items={widgetOrder} strategy={verticalListSortingStrategy}>
                        <div className="space-y-2 mb-6">
                            {widgetOrder.map((id, idx) => {
                                const widget = AVAILABLE_WIDGETS.find(w => w.id === id);
                                if (!widget) return null;
                                return (
                                    <SortableConfigItem 
                                        key={id} 
                                        id={id} 
                                        idx={idx} 
                                        widget={widget} 
                                        isVisible={widgetVisibility[id] !== false} 
                                        onToggle={onToggle} 
                                    />
                                );
                            })}
                        </div>
                    </SortableContext>
                </DndContext>
                <div className="flex gap-3">
                    <button onClick={onReset} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-bold text-gray-700 hover:bg-gray-50 transition">
                        <i className="fas fa-undo mr-1"></i> Reset
                    </button>
                    <button onClick={onClose} className="flex-1 px-4 py-2.5 bg-emerald-600 rounded-xl text-sm font-bold text-white hover:bg-emerald-700 transition">
                        <i className="fas fa-check mr-1"></i> Done
                    </button>
                </div>
            </div>
        </div>
    );
}

interface DashboardWidgetsProps {
    user: any;
    selectedGroupId: string;
}

const DashboardWidgets: React.FC<DashboardWidgetsProps> = ({ user, selectedGroupId }) => {
    const navigate = useNavigate();
    const [widgetOrder, setWidgetOrder] = useState<string[]>(loadWidgetOrder);
    const [widgetVisibility, setWidgetVisibility] = useState<Record<string, boolean>>(loadWidgetVisibility);
    const [configOpen, setConfigOpen] = useState(false);
    // Seed with a zero-state skeleton so the widget grid renders on the
    // FIRST paint instead of replacing everything with a single spinner.
    // Every slice below hydrates independently as its fetch resolves.
    const EMPTY_DASHBOARD = {
        stats: {
            totalProperties: 0, complianceScore: 0, compliantProperties: 0,
            atRiskProperties: 0, vacantCount: 0, occupiedCount: 0,
            vacantResidential: 0, vacantHMO: 0, partialHMO: 0,
            expiringSoon: 0, overdue: 0,
        },
        properties: [], certificates: [], recentTenants: [], recentActivity: [],
        exposureScore: null, predictiveAlerts: null,
        arrearsCount: 0, arrearsAlerts: [], totalArrears: 0, collectionRate: 100,
        criticalCount: 0, overdueCount: 0, upcomingCount: 0,
    };
    const [dashboardData, setDashboardData] = useState<any>(EMPTY_DASHBOARD);
    // No global loading flag — widgets render instantly with the skeleton
    // and each call merges in as it resolves. This is the Properties-page
    // pattern: paint first, hydrate progressively.

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

    useEffect(() => {
        const groupParam = selectedGroupId === 'all' ? undefined : selectedGroupId;

        // Reset to skeleton so a group switch shows a clean state
        // immediately, then re-hydrates.
        setDashboardData(EMPTY_DASHBOARD);

        let cancelled = false;
        // Small retry-on-5xx wrapper to absorb transient Lambda cold-start
        // failures. Most warm Lambdas respond in <300ms, but the first hit
        // after a deploy or quiet period can 500 once before settling.
        const withRetry = async (fn: () => Promise<any>, label: string) => {
            for (let attempt = 0; attempt < 2; attempt++) {
                try { return await fn(); }
                catch (e: any) {
                    const status = e?.response?.status || 0;
                    const transient = !status || (status >= 500 && status < 600);
                    if (attempt === 0 && transient) {
                        await new Promise(r => setTimeout(r, 600));
                        continue;
                    }
                    console.warn(`[Dashboard] ${label} fetch failed:`, e?.message);
                    return null;
                }
            }
        };
        const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

        // Stagger requests in 3 waves so we don't trigger a cold-start
        // storm on Lambda (8 parallel requests previously caused 4 cold
        // starts at once, queuing some into 5xx). Stats first → user sees
        // the top counters within ~500ms. Then heavy calls in a smaller
        // batch. Then the slowest one. Each call still hydrates its own
        // slice independently when it lands.

        // 1. Stats — drives the top counters. Fastest endpoint. Fire and
        //    merge as soon as it lands. NO await blocking the render.
        complianceService.getDashboardStats(groupParam)
            .then((res: any) => {
                const s = res?.data?.data?.summary || {};
                const certs = res?.data?.data?.certificates || {};
                setDashboardData((prev: any) => ({
                    ...prev,
                    stats: {
                        totalProperties:     s.totalProperties     || 0,
                        complianceScore:     s.score               || 0,
                        compliantProperties: s.compliantProperties || 0,
                        atRiskProperties:    s.atRiskProperties    || 0,
                        vacantCount:         s.vacantCount         || 0,
                        occupiedCount:       s.occupiedCount       || 0,
                        vacantResidential:   s.vacantResidential   || 0,
                        vacantHMO:           s.vacantHMO           || 0,
                        partialHMO:          s.partialHMO          || 0,
                        expiringSoon:        certs.expiring        || 0,
                        overdue:             certs.overdue         || 0,
                    },
                }));
            })
            .catch((e: any) => console.warn('[Dashboard] stats fetch failed:', e?.message));

        // 2. Exceptions — drives recent activity. Fast endpoint.
        complianceService.getExceptionFeed(groupParam)
            .then((res: any) => {
                const exceptions = res?.data?.data || [];
                const alerts: any[] = [];
                exceptions.forEach((e: any) => {
                    if (e.priority === 'critical' || e.priority === 'warning') {
                        alerts.push({ type: 'warning', message: e.issue, detail: e.propertyName });
                    }
                });
                if (alerts.length === 0) alerts.push({ type: 'success', message: 'System Status Healthy', detail: 'No immediate actions required' });
                setDashboardData((prev: any) => ({ ...prev, recentActivity: alerts.slice(0, 5) }));
            })
            .catch((e: any) => console.warn('[Dashboard] exceptions fetch failed:', e?.message));

        // 3. Heavy data — staggered into two waves so we don't hit Lambda
        //    with 8 parallel cold starts at once (was producing 5xx for
        //    the queued requests on large portfolios).
        (async () => {
            await sleep(250);
            if (cancelled) return;

            // Wave A: properties + tenants together (the dashboard's
            // vacancy + arrears widgets depend on these).
            withRetry(() => complianceService.getProperties(), 'properties').then((res: any) => {
                if (cancelled || !res) return;
                const raw = res?.data?.data || res?.data || [];
                setDashboardData((prev: any) => {
                    const occupied = new Set((prev.recentTenants || [])
                        .filter((t: any) => t.propertyId).map((t: any) => t.propertyId));
                    const withVacancy = (Array.isArray(raw) ? raw : []).map((p: any) => ({
                        ...p,
                        _vacant: !occupied.has(p.id) && ['vacant', 'empty', 'unoccupied']
                            .includes((p.status || p.occupancyStatus || '').toLowerCase()),
                    }));
                    return { ...prev, properties: withVacancy };
                });
            });

            withRetry(() => tenantService.getTenants(), 'tenants').then((res: any) => {
                if (cancelled || !res) return;
                const tenants = res?.data?.data || res?.data || [];
                setDashboardData((prev: any) => {
                    const occupied = new Set(tenants
                        .filter((t: any) => t.propertyId).map((t: any) => t.propertyId));
                    const reVacant = (prev.properties || []).map((p: any) => ({
                        ...p,
                        _vacant: !occupied.has(p.id) && ['vacant', 'empty', 'unoccupied']
                            .includes((p.status || p.occupancyStatus || '').toLowerCase()),
                    }));
                    return { ...prev, recentTenants: tenants, properties: reVacant };
                });
            });

            await sleep(400);
            if (cancelled) return;

            // Wave B: certificates + exposure + predictive
            withRetry(() => complianceService.getLandlordCertificates(), 'certs').then((res: any) => {
                if (cancelled || !res) return;
                setDashboardData((prev: any) => ({ ...prev, certificates: res?.data?.data || res?.data || [] }));
            });

            withRetry(() => complianceService.getExposureScore(), 'exposure').then((res: any) => {
                if (cancelled || !res) return;
                setDashboardData((prev: any) => ({ ...prev, exposureScore: res?.data?.data || null }));
            });

            withRetry(() => complianceService.getPredictiveAlerts(), 'predictive').then((res: any) => {
                if (cancelled || !res) return;
                setDashboardData((prev: any) => ({ ...prev, predictiveAlerts: res?.data?.data || null }));
            });

            await sleep(400);
            if (cancelled) return;

            // Wave C: arrears — the slowest endpoint (iterates every
            // tenant). The widget renders 0s until this lands.
            withRetry(() => arrearsService.getDashboard(), 'arrears').then((arrRes: any) => {
                if (cancelled) return;
                const a = arrRes?.data?.data;
                if (!a) return;
                setDashboardData((prev: any) => ({
                    ...prev,
                    arrearsCount:   a.inArrearsCount  ?? prev.arrearsCount,
                    arrearsAlerts:  a.alerts          ?? prev.arrearsAlerts,
                    totalArrears:   a.totalArrears    ?? prev.totalArrears,
                    collectionRate: a.collectionRate  ?? prev.collectionRate,
                    criticalCount:  a.criticalCount   ?? prev.criticalCount,
                    overdueCount:   a.overdueCount    ?? prev.overdueCount,
                    upcomingCount:  a.upcomingCount   ?? prev.upcomingCount,
                }));
            });
        })();

        return () => { cancelled = true; };
    }, [selectedGroupId]);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            setWidgetOrder(prev => {
                const oldIndex = prev.indexOf(active.id as string);
                const newIndex = prev.indexOf(over.id as string);
                const next = arrayMove(prev, oldIndex, newIndex);
                saveWidgetOrder(next);
                return next;
            });
        }
    }, []);

    const toggleWidget = useCallback((id: string) => {
        setWidgetVisibility(prev => {
            const next = { ...prev, [id]: prev[id] === false };
            saveWidgetVisibility(next);
            return next;
        });
    }, []);

    const resetDefaults = useCallback(() => {
        localStorage.removeItem(WIDGET_CONFIG_KEY);
        localStorage.removeItem('hp_widget_visibility');
        setWidgetOrder(AVAILABLE_WIDGETS.map(w => w.id));
        setWidgetVisibility({});
    }, []);

    // Compliance Reporting is a Super Admin per-account toggle, not part of
    // the personal drag/hide customisation every other widget here uses —
    // an account it's switched off for never sees it at all, regardless of
    // the user's own saved widgetOrder/widgetVisibility.
    const isTexasBuild = getAppMarket() === 'texas';

    const enabledWidgetOrder = widgetOrder.filter(id => {
        if ((id === 'repairs-widget' || id === 'awaab-law-widget') && !featureFlags.COMPLIANCE_REPORTING_WIDGET_ENABLED) return false;
        if (isTexasBuild && id === 'awaab-law-widget') return false;
        if (!isTexasBuild && id.startsWith('texas-')) return false;
        return true;
    });
    const visibleWidgets = enabledWidgetOrder.filter(id => widgetVisibility[id] !== false);
    const itemIds = enabledWidgetOrder.filter(id => widgetVisibility[id] !== false);

    // No global loading gate — widgets render with the EMPTY_DASHBOARD
    // skeleton on first paint and hydrate progressively. Same pattern as
    // the Properties page (first 50 cards visible in <2s).

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-slate-800">
                    <i className="fas fa-grip text-emerald-500 mr-2"></i>
                    Dashboard Widgets
                </h2>
                <button
                    onClick={() => setConfigOpen(true)}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-900 text-white rounded-lg text-[10px] font-bold hover:bg-slate-800 transition shadow-sm"
                >
                    <i className="fas fa-sliders"></i>
                    Customise
                </button>
            </div>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                        {visibleWidgets.map(id => {
                            switch (id) {
                                case 'exposure-score':
                                    return <SortableWidget key={id} id={id}><ExposureScoreWidget exposureScore={dashboardData.exposureScore} /></SortableWidget>;
                                case 'predictive-alerts':
                                    return <SortableWidget key={id} id={id}><PredictiveAlertsWidget predictiveAlerts={dashboardData.predictiveAlerts} /></SortableWidget>;
                                case 'live-exceptions':
                                    return (
                                        <SortableWidget key={id} id={id}>
                                            <CompactWidget title="Live Exceptions" icon="fa-triangle-exclamation" iconColor="rose" defaultExpanded={true}>
                                                <div className="max-h-96 overflow-y-auto">
                                                    <ExceptionFeed groupId={selectedGroupId === 'all' ? undefined : selectedGroupId} />
                                                </div>
                                            </CompactWidget>
                                        </SortableWidget>
                                    );
                                case 'portfolio-health':
                                    return <SortableWidget key={id} id={id}><PortfolioHealthWidget stats={dashboardData.stats} properties={dashboardData.properties} certificates={dashboardData.certificates} /></SortableWidget>;
                                case 'legal-updates':
                                    return (
                                        <SortableWidget key={id} id={id}>
                                            <CompactWidget title="Legal Updates" icon="fa-gavel" iconColor="blue" defaultExpanded={true}>
                                                <div className="max-h-96 overflow-y-auto">
                                                    <LegalUpdates />
                                                </div>
                                            </CompactWidget>
                                        </SortableWidget>
                                    );
                                case 'rent-arrears':
                                    return <SortableWidget key={id} id={id}><RentArrearsWidget dashboardData={dashboardData} onTenantClick={(tenantId) => navigate(`/arrears?tenant=${tenantId}`)} /></SortableWidget>;
                                case 'repairs-widget':
                                    return <SortableWidget key={id} id={id}><RepairsWidgetCompact /></SortableWidget>;
                                case 'awaab-law-widget':
                                    return <SortableWidget key={id} id={id}><AwaabLawWidgetCompact /></SortableWidget>;
                                case 'texas-deposit-tracker':
                                    return (
                                        <SortableWidget key={id} id={id}>
                                            <DepositTrackerWidget
                                                onDepositClick={(d) => navigate(`/properties?deposit=${d.id}`)}
                                                onPropertyClick={(pid) => navigate(`/properties/${pid}`)}
                                            />
                                        </SortableWidget>
                                    );
                                case 'texas-rekey-status':
                                    return (
                                        <SortableWidget key={id} id={id}>
                                            <RekeyStatusWidget
                                                onUnitClick={(u) => navigate(`/properties/${u.propertyId}`)}
                                                onPropertyClick={(pid) => navigate(`/properties/${pid}`)}
                                            />
                                        </SortableWidget>
                                    );
                                case 'texas-rental-registration':
                                    return (
                                        <SortableWidget key={id} id={id}>
                                            <RentalRegistrationWidget
                                                onRegistrationClick={(r) => navigate(`/properties/${r.propertyId}`)}
                                                onPropertyClick={(pid) => navigate(`/properties/${pid}`)}
                                            />
                                        </SortableWidget>
                                    );
                                case 'recent-activity':
                                    return (
                                        <SortableWidget key={id} id={id}>
                                            <CompactWidget title="Recent Activity" icon="fa-clock-rotate" iconColor="blue" badge={<span className="text-xs text-gray-400">{dashboardData.recentActivity.length}</span>}>
                                                <div className="space-y-2">
                                                    {dashboardData.recentActivity.map((item: any, idx: number) => (
                                                        <div key={idx} className={`flex items-center gap-2 p-2 rounded-lg ${item.type === 'warning' ? 'bg-yellow-50' : 'bg-green-50'}`}>
                                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${item.type === 'warning' ? 'bg-yellow-200 text-yellow-700' : 'bg-green-200 text-green-700'}`}>
                                                                <i className={`fas fa-${item.type === 'warning' ? 'exclamation' : 'check'} text-[10px]`}></i>
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-xs font-medium text-gray-900 truncate">{item.message}</p>
                                                                <p className="text-[10px] text-gray-500 truncate">{item.detail}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </CompactWidget>
                                        </SortableWidget>
                                    );
                                default:
                                    return null;
                            }
                        })}
                    </div>
                </SortableContext>
            </DndContext>

            <WidgetConfigModal
                isOpen={configOpen}
                onClose={() => setConfigOpen(false)}
                widgetOrder={enabledWidgetOrder}
                widgetVisibility={widgetVisibility}
                onToggle={toggleWidget}
                onReset={resetDefaults}
                onDragEnd={handleDragEnd}
            />
        </div>
    );
};

export default DashboardWidgets;
